import React from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';

const BRAND = '#6318C6';
const BRAND_LIGHT = '#f3eeff';
const BRAND_MID = '#e9d5ff';

export default function UsageLimitExceededModal({ open, onClose, message, limit, used, showWhatsAppSmsAuth = true }) {
  const navigate = useNavigate();

  const handleBuyAddon = () => {
    onClose();
    navigate('/credits?tab=0');
  };

  const usedCount = used ?? limit;
  const pct = limit ? Math.min(100, Math.round((usedCount / limit) * 100)) : 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(99,24,198,0.18)',
        },
      }}
    >
      {/* Purple gradient header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND} 0%, #9333ea 100%)`,
          pt: 4,
          pb: 3,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'rgba(255,255,255,0.7)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Icon bubble */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            border: '2px solid rgba(255,255,255,0.3)',
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 32, color: '#fff' }} />
        </Box>

        <Typography variant="h6" fontWeight={700} color="#fff" textAlign="center">
          Usage Limit Reached
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5, textAlign: 'center' }}>
          You've hit your plan limit for this period
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        {/* Message */}
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 2, lineHeight: 1.6 }}
        >
          {message || 'You have reached your usage limit for this billing period.'}
        </Typography>

        {/* Usage bar */}
        {limit != null && (
          <Box
            sx={{
              bgcolor: BRAND_LIGHT,
              border: `1px solid ${BRAND_MID}`,
              borderRadius: '12px',
              p: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" fontWeight={600} color={BRAND}>
                Usage
              </Typography>
              <Typography variant="caption" fontWeight={700} color={BRAND}>
                {usedCount} / {limit}
              </Typography>
            </Box>
            {/* Progress bar */}
            <Box sx={{ height: 6, bgcolor: BRAND_MID, borderRadius: 3, overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${pct}%`,
                  borderRadius: 3,
                  background: `linear-gradient(90deg, ${BRAND}, #9333ea)`,
                }}
              />
            </Box>
          </Box>
        )}

        {/* Upgrade nudge */}
        {showWhatsAppSmsAuth && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: '#fafafa',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              p: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                bgcolor: BRAND_LIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 18, color: BRAND }} />
            </Box>
            <Typography variant="caption" color="text.secondary" lineHeight={1.5}>
              Purchase an addon to unlock more usage for this billing period.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <Box sx={{ px: 3, pb: 3, pt: 1.5, display: 'flex', gap: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: '#e0e0e0',
            color: '#555',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.2,
            '&:hover': { borderColor: '#bbb', bgcolor: '#fafafa' },
          }}
        >
          Close
        </Button>
        {showWhatsAppSmsAuth && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleBuyAddon}
            sx={{
              background: `linear-gradient(135deg, ${BRAND} 0%, #9333ea 100%)`,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              py: 1.2,
              boxShadow: '0 4px 14px rgba(99,24,198,0.35)',
              '&:hover': {
                background: `linear-gradient(135deg, #5010b0 0%, #7e22ce 100%)`,
                boxShadow: '0 6px 20px rgba(99,24,198,0.45)',
              },
            }}
          >
            Buy Addon
          </Button>
        )}
      </Box>
    </Dialog>
  );
}
